import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Pdf } from '@range.io/basic-types'
import React, { useEffect } from 'react'
import { useSelector, useStore } from 'react-redux'
import { v4 } from 'uuid'
import { useKeyMaps } from '../components-reusable/hooks'

import {
    Box,
    Button,
    DatePicker,
    FlexColumn,
    FlexRow,
    Icon,
    SingleSelectUser,
    Text,
} from '../components-reusable/index.js'
import featureFlags from '../feature-flags.js'
import * as httpsCalls from '../firebase/commands/https-calls.js'
import { CollaborationChangedCommand } from '../firebase/commands/index.js'
import { useCommandHistory } from '../firebase/commands/UndoRedo.js'
import { styled } from '../range-theme/index.js'
import { ReduxActions, ReduxSelectors } from '../redux/index.js'
import * as Segment from '../segment/segment.js'
import { BulkCategorySelector } from './CategorySelector.js'
import CreatingReportInfoModal from './CreatingReportInfoModal'
import EditTagsModal from './EditTagsModal.js'
import { FollowButton } from './FollowButton.js'
import FollowersSelector from './FollowersSelector.js'
import ListViewArchiveTasksModal from './ListViewArchiveTasksModal.js'
import ListViewDeleteTasksModal from './ListViewDeleteTasksModal.js'
import SelectReportTemplateModal from './reports/SelectReportTemplateModal.js'
import useReport from './reports/TaskListReport'
import StatusSelector from './StatusSelector.js'

const BulkActionsHeaderWrapper = styled(FlexColumn, {
    width: '100%',
    color: '$neutral04',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '21px',
    padding: '16px',
    borderTop: '1px solid $neutral07',
    background: '$neutral08',
    position: 'relative',
})

const ControlsContainer = styled(FlexRow, {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
})

const CollaborationSelectedCountLabel = styled(Text, {
    color: '$neutral04',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '18px',
})

const ListWrapper = styled(FlexColumn, {
    mt: '44px',
    background: '$neutral10',
    color: '$neutral05',
    border: '1px solid $neutral07',
    borderRadius: '6px',
    position: 'absolute',
    zIndex: 200,
    boxShadow: '0px 6px 10px #00000010',
    boxSizing: '100%',
})

const StyledDropdownContent = styled(DropdownMenu.Content, {
    backgroundColor: '$primary04',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px',
    marginTop: '8px',
    borderRadius: '6px',
    boxShadow: '0px 4px 8px rgba(0, 4, 4, 0.1)',
    width: 'var(--radix-dropdown-menu-trigger-width)',
})

const StyledMenuItemContainer = styled(DropdownMenu.Item, {
    padding: 0,
    margin: 0,
    outline: 'none',
    cursor: 'pointer',
    borderRadius: '4px',

    '&[data-highlighted]': {
        backgroundColor: '$primary06',
        '& button': {
            backgroundColor: '$primary06',
        },
    },
})

const StyledMenuItem = styled(Button, {
    all: 'unset',
    display: 'flex',
    gap: '8px',
    padding: '0px 10px',
    height: '40px',
    alignItems: 'center',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'transparent',

    '& span': {
        color: 'white',
    },

    '& svg': {
        color: 'white',
    },
})

/**
 * Menu Button for report creation
 * @sig ReportMenuButton :: {Boolean, Func, Func} -> ReactElement
 *   Func = () -> ()
 */
const ReportMenuButton = ({ disabled, createPdfReport, createWordReport }) => (
    <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
            <Button variant="primary" size="lg" disabled={disabled}>
                <Icon name="collaboration" iconSize="18px" />
                <Text>Create Report</Text>
                <Icon name="chevronDownNew" iconSize="16px" />
            </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
            <StyledDropdownContent side="bottom" align="start">
                {featureFlags().ran4155AllowWordReports && (
                    <StyledMenuItemContainer>
                        <StyledMenuItem onClick={createWordReport}>
                            <Icon name="reportWord" iconSize="18px" />
                            <Text>Word Report</Text>
                        </StyledMenuItem>
                    </StyledMenuItemContainer>
                )}
                <StyledMenuItemContainer>
                    <StyledMenuItem onClick={createPdfReport}>
                        <Icon name="reportPdf" iconSize="18px" />
                        <Text>PDF Report</Text>
                    </StyledMenuItem>
                </StyledMenuItemContainer>
            </StyledDropdownContent>
        </DropdownMenu.Portal>
    </DropdownMenu.Root>
)

const ListViewBulkActions = ({
    hasRightsToArchive,
    hasRightsToDelete,
    checkedItems,
    onClearAllClick,
    onDelete,
    dueDate,
    onDueDateChanged,
    onDeleteItems,
    onArchiveItems,
    allAvailableAssignees,
    bulkActionSelectedAssignee,
    onBulkActionSelectedAssignee,
    areAllCheckedTasksArchived = false,
    areAllTasksFollowed = false,
    isFollowingInProgress = false,
    statusNames,
    bulkActionSelectedStatus,
    onBulkActionSelectedStatus,
    onBulkActionTagsChange,
    onBulkActionDateChange,
    onBulkActionFollowersChange,
    onBulkFollowButtonClick,
    onExport,
    toReports,
    selectedProject,
}) => {
    const resetReport = () => {
        reportPromiseRef?.current && reportPromiseRef.current.cancel()
        setIsReportInProgress(false)
    }

    const closeActiveElements = () => {
        setShowDeleteTasksModal(false)
        setShowArchiveTasksModal(false)
        setShowTagsEditModal(false)
        setShowAssigneeList(false)
        setShowStatusList(false)
        setShowDatePicker(false)
        document.activeElement.blur()
    }

    const toggleAssignee = userId => {
        onBulkActionSelectedAssignee(userId)
        setShowAssigneeList(false)
    }

    const hideTagsModal = () => setShowTagsEditModal(false)

    const onEditTagsConfirm = (editType, items) => {
        onBulkActionTagsChange({ items, editType })
        hideTagsModal()
    }

    // put up the template selection modal
    const selectWordReportTemplate = async () => {
        if (!featureFlags().ran4155AllowWordReports) return

        setShowTemplateModal(true)
    }

    /*
     * Create a word report using the specified template name
     */
    const createWordReport = async (template, isCustomTemplate) => {
        setShowTemplateModal(false)
        setIsReportInProgress(true)
        try {
            const wordDoc = await httpsCalls.createDocx(selectedProject.id, template, isCustomTemplate, checkedItems)
            const link = document.createElement('a')
            link.href = URL.createObjectURL(wordDoc)
            link.download = `${template}.docx`
            link.click()
        } catch (e) {
            const description =
                "This doesn't usually happen, but we were unable to produce your report. We apologise for the inconvenience."
            const title = 'Oops... something went wrong'
            dispatch(ReduxActions.globalModalDataSet({ title, description, cancelButton: { label: 'Ok' } }))
        }
        setIsReportInProgress(false)
    }

    const createPdfReport = () => {
        setIsReportInProgress(true)
        // delay it a bit so React gets time to update and show the modal before starting the creation of the PDF
        setTimeout(() => {
            const newReportPromise = prepareReport(checkedItems)
            reportPromiseRef.current = newReportPromise
            reportPromiseRef.current.promise.then(async reportBlob => {
                const segmentParams = ReduxSelectors.paramsForTrackEvent(getState())
                segmentParams.projectName = selectedProject.name
                segmentParams.taskCount = checkedItems.length
                segmentParams.pageCount = await Pdf.getPdfPageCount(reportBlob)
                Segment.sendTrack('task report created', v4(), segmentParams)

                openReport(reportBlob)
                setIsReportInProgress(false)
            })
        }, 10)
    }

    const showToast = (id, toastLabel) =>
        dispatch(ReduxActions.toastAdded({ id, toastLabel, severity: 'success', showUndo: false }))

    /*
     * bulk-update categories of selected collaborations and show a toast
     */
    const onBulkActionCategoryChanged = category => {
        const checkedCollaborations = allCollaborations.pick(checkedItems)
        const changedCollaborations = checkedCollaborations.filter(c => c.categoryId !== category.id)
        const count = changedCollaborations.length

        if (count === 0) return

        const command = CollaborationChangedCommand.Outbound
        const changes = { categoryId: category.id }
        const toastLabel = `The Category for ${count} ${count === 1 ? 'task' : 'tasks'} has been successfully updated`

        dispatch(ReduxActions.collaborationsChanged({ collaborations: changedCollaborations, changes }))
        changedCollaborations.map(c => runCommand(command(c.id, { categoryId: category.id }, userId)))
        showToast(changedCollaborations[0].id, toastLabel)
    }

    // close active elements on Escape key down
    useEffect(() => {
        pushKeyMap('ListViewBulkActions', { Escape: closeActiveElements })
        return () => popKeyMap('ListViewBulkActions')
    }, [])

    // check for mousedown outside the list and stop showing the list
    useEffect(() => {
        const listener = event => {
            if (!listRef?.current || !listRef.current.contains(event.target)) setShowAssigneeList(false)
        }
        document.addEventListener('mousedown', listener)
        return () => document.removeEventListener('mousedown', listener)
    }, [])

    useEffect(() => {
        // we want to reset the report whenever the content for it changed (here, the amount of selected tasks)
        resetReport()
    }, [checkedItems])

    const singleSelectUserProps = {
        projectParticipants: allAvailableAssignees,
        selectedUser: bulkActionSelectedAssignee,
        setSelectedUserId: toggleAssignee,
    }

    const checkedTasksCountText =
        checkedItems.length > 1 ? `${checkedItems.length} tasks selected` : `${checkedItems.length} task selected`

    const [showDeleteTasksModal, setShowDeleteTasksModal] = React.useState()
    const [showArchiveTasksModal, setShowArchiveTasksModal] = React.useState()
    const [showTagsEditModal, setShowTagsEditModal] = React.useState()
    const [showAssigneeList, setShowAssigneeList] = React.useState()
    const [showStatusList, setShowStatusList] = React.useState()
    const [showDatePicker, setShowDatePicker] = React.useState()
    const [participantsForFollowersSelector, setParticipantsForFollowersSelector] = React.useState([])
    const listRef = React.useRef()
    const { pushKeyMap, popKeyMap } = useKeyMaps()
    const { prepareReport, openReport } = useReport()
    const [isReportInProgress, setIsReportInProgress] = React.useState(false)
    const reportPromiseRef = React.useRef(null)
    const { dispatch, getState } = useStore()
    const allCollaborations = useSelector(ReduxSelectors.collaborationsAsLookupTable)
    const { runCommand } = useCommandHistory()
    const userId = ReduxSelectors.selectedUserId(getState())
    const [showTemplateModal, setShowTemplateModal] = React.useState(false)

    useEffect(() => {
        const toFollowersSelectorItem = participant => {
            return {
                value: participant.id,
                participant,
            }
        }

        const participantsInFollowerFormat = allAvailableAssignees.map(toFollowersSelectorItem) // map it to follower format, so we get online state and value for the list

        setParticipantsForFollowersSelector(participantsInFollowerFormat)
    }, [allAvailableAssignees])

    return (
        <BulkActionsHeaderWrapper data-cy="list-view-bulk-actions">
            {showDeleteTasksModal && (
                <ListViewDeleteTasksModal onCancel={() => setShowDeleteTasksModal(false)} onDelete={onDeleteItems} />
            )}
            {showArchiveTasksModal && (
                <ListViewArchiveTasksModal
                    onCancel={() => setShowArchiveTasksModal(false)}
                    onArchive={() => {
                        onArchiveItems()
                        setShowArchiveTasksModal(false)
                    }}
                    areAllCheckedTasksArchived={areAllCheckedTasksArchived}
                />
            )}
            {showTagsEditModal && (
                <EditTagsModal
                    onCancel={hideTagsModal}
                    onConfirm={onEditTagsConfirm}
                    onDelete={hideTagsModal}
                    addTagsDescription="Add additional, new tags to the selected tasks."
                    replaceTagsDescription="Replace ALL existing tags with new tags for the selected tasks. ALL existing tags will
                be removed and replaced with new tags."
                />
            )}
            {isReportInProgress && <CreatingReportInfoModal onCancel={resetReport} />}
            {showTemplateModal && (
                <SelectReportTemplateModal
                    onTemplateSelect={createWordReport}
                    onCancel={() => setShowTemplateModal(false)}
                    toReports={toReports}
                />
            )}
            <ControlsContainer>
                <CollaborationSelectedCountLabel data-cy="bulk-actions-tasks-count">
                    {checkedTasksCountText}
                </CollaborationSelectedCountLabel>
                <ReportMenuButton
                    disabled={isReportInProgress}
                    createPdfReport={createPdfReport}
                    createWordReport={selectWordReportTemplate}
                />

                <FlexColumn>
                    <Button variant="secondary" size="lg" onClick={() => setShowDatePicker(val => !val)}>
                        <Icon name="calendar" iconSize="16px" />
                        <Text>Due Date</Text>
                    </Button>

                    {showDatePicker && (
                        <Box css={{ position: 'absolute', mt: '44px', zIndex: 200 }}>
                            <DatePicker
                                onChange={dueDate => {
                                    onBulkActionDateChange(dueDate)
                                    setShowDatePicker(false)
                                }}
                                value={dueDate}
                            />
                        </Box>
                    )}
                </FlexColumn>

                <Button variant="secondary" size="lg" onClick={() => setShowTagsEditModal(val => !val)}>
                    <Icon name="tags" iconSize="16px" />
                    <Text>Bulk Tag Editor</Text>
                </Button>
                <FlexColumn ref={listRef} css={{ maxWidth: '120px' }}>
                    {showAssigneeList ? (
                        <SingleSelectUser
                            withoutIcon
                            withNoAssigneeOption
                            css={{ height: '40px' }}
                            {...singleSelectUserProps}
                        />
                    ) : (
                        <Button variant="secondary" size="lg" onClick={() => setShowAssigneeList(val => !val)}>
                            <Icon name="user" iconSize="16px" />
                            <Text>Assignee</Text>
                        </Button>
                    )}
                </FlexColumn>
                <FlexColumn css={{ maxWidth: '120px' }}>
                    <BulkCategorySelector setSelectedCategory={onBulkActionCategoryChanged} />
                </FlexColumn>
                <FlexColumn>
                    <Button variant="secondary" size="lg" onClick={() => setShowStatusList(true)}>
                        <Icon name="status" iconSize="16px" />
                        <Text>Status</Text>
                    </Button>
                    {showStatusList && (
                        <ListWrapper ref={listRef}>
                            <StatusSelector
                                displayAsList
                                allStatusNames={statusNames}
                                initialStatusName={statusNames[bulkActionSelectedStatus]}
                                onStatusChanged={onBulkActionSelectedStatus}
                                onOpenChange={isOpen => setShowStatusList(isOpen)}
                                defaultOpen={showStatusList}
                            />
                        </ListWrapper>
                    )}
                </FlexColumn>
                {hasRightsToArchive && (
                    <Button variant="secondary" size="lg" onClick={() => setShowArchiveTasksModal(true)}>
                        <Icon name="archive" iconSize="16px" />
                        <Text>{areAllCheckedTasksArchived ? 'Unarchive' : 'Archive'}</Text>
                    </Button>
                )}
                <FollowButton
                    onClick={onBulkFollowButtonClick}
                    isFollowing={areAllTasksFollowed}
                    disabled={isFollowingInProgress}
                />
                <FollowersSelector
                    multiSelect
                    participants={participantsForFollowersSelector}
                    followerIds={[]}
                    preventAutoClosing
                    isLoading={isFollowingInProgress}
                    isToggleMode={true}
                    showFollowersCount={false}
                    onToggleModeSave={onBulkActionFollowersChange}
                    trigger={
                        <Button variant="secondary" size="lg" onClick={() => setShowStatusList(true)}>
                            <Icon name="followers" iconSize="16px" />
                            <Text>Edit Followers</Text>
                        </Button>
                    }
                />
                <Button variant="secondary" size="lg" onClick={onExport}>
                    <Icon name="exportAction" iconSize="16px" />
                    <Text>Export</Text>
                </Button>
                {hasRightsToDelete && (
                    <Button
                        css={{ pr: '12px', gap: '4px' }}
                        variant="destructiveSecondary"
                        size="lg"
                        onClick={() => setShowDeleteTasksModal(true)}
                    >
                        <Icon name="trash" iconSize="16px" />
                        <Text>Delete</Text>
                    </Button>
                )}
                <Button css={{ ml: 'auto' }} variant="secondary" size="lg" onClick={onClearAllClick}>
                    <Text>Done</Text>
                </Button>
            </ControlsContainer>
        </BulkActionsHeaderWrapper>
    )
}

export default ListViewBulkActions
